/**
 * Discount code field for the cart drawer and cart page.
 *
 * Codes are applied with the storefront cart API (`discount` on
 * /cart/update.js). The same request renders the drawer, the cart page, the
 * header bubble and the entries chip, so one round trip updates everything.
 * The drawer markup is replaced on each render, so the component keeps no
 * state of its own beyond the request in flight.
 */
(() => {
  if (window.FFDiscountCode) return;

  const ENFORCE_HEADER = 'X-FF-Multiplier-Enforce';

  const readJSON = (root, selector, fallback) => {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      return fallback;
    }
  };

  /** Containers on this page that a cart response can re-render. */
  function getRenderTargets() {
    const targets = [];

    const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
    if (drawerInner) targets.push({ section: 'cart-drawer', selector: '.drawer__inner', el: drawerInner });

    const mainItems = document.getElementById('main-cart-items');
    const mainContents = mainItems && mainItems.querySelector('.js-contents');
    if (mainItems && mainContents && mainItems.dataset.id) {
      targets.push({ section: mainItems.dataset.id, selector: '.js-contents', el: mainContents });
    }

    const footer = document.getElementById('main-cart-footer');
    const footerContents = footer && footer.querySelector('.js-contents');
    if (footer && footerContents && footer.dataset.id) {
      targets.push({ section: footer.dataset.id, selector: '.js-contents', el: footerContents });
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) targets.push({ section: 'cart-icon-bubble', selector: '.shopify-section', el: bubble });

    const chip = document.querySelector('ff-header-entries');
    if (chip) targets.push({ section: 'ff-header-entries', selector: 'ff-header-entries', el: chip, chip: true });

    return targets;
  }

  function applySections(sections, targets) {
    if (!sections) return;
    for (const target of targets) {
      const html = sections[target.section];
      if (!html) continue;
      const found = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (!found) continue;
      if (target.chip) target.el.dataset.total = found.dataset.total || '';
      target.el.innerHTML = found.innerHTML;
    }
  }

  async function updateDiscounts(codes) {
    const targets = getRenderTargets();
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [ENFORCE_HEADER]: '1',
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({
        discount: codes.join(','),
        sections: targets.map((t) => t.section),
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error(`cart/update.js failed: ${res.status}`);

    const data = await res.json();
    applySections(data.sections, targets);
    document.dispatchEvent(new CustomEvent('ff:entries:updated', { detail: { cart: data } }));
    return data;
  }

  const sameCode = (a, b) => String(a).trim().toUpperCase() === String(b).trim().toUpperCase();

  class FFDiscountCode extends HTMLElement {
    #busy = false;

    connectedCallback() {
      this.addEventListener('submit', (event) => this.#onSubmit(event));
      this.addEventListener('click', (event) => this.#onClick(event));
    }

    get #context() {
      return this.dataset.context || 'drawer';
    }

    get #applied() {
      const codes = readJSON(this, '[data-ff-discount-codes]', []);
      return Array.isArray(codes) ? codes.filter(Boolean) : [];
    }

    get #strings() {
      return readJSON(this, '[data-ff-discount-strings]', {});
    }

    /** The component is rebuilt on every render; find the live one. */
    #fresh() {
      return document.querySelector(`ff-discount-code[data-context="${this.#context}"]`) || this;
    }

    #setBusy(busy) {
      this.#busy = busy;
      const form = this.querySelector('[data-ff-discount-form]');
      if (form) form.toggleAttribute('aria-busy', busy);
      for (const btn of this.querySelectorAll('button')) btn.disabled = busy;
    }

    #showMessage(text, { code = '' } = {}) {
      const el = this.#fresh();
      const msg = el.querySelector('.ff-discount__msg');
      const input = el.querySelector('.ff-discount__input');
      const details = el.querySelector('.ff-discount__details');
      if (msg) {
        msg.textContent = text;
        msg.hidden = !text;
      }
      // A rejected code reopens the field on the re-rendered markup so the
      // shopper can correct it.
      if (details && text) details.open = true;
      if (input) {
        input.value = code;
        input.setAttribute('aria-invalid', text ? 'true' : 'false');
        if (text) input.focus();
      }
    }

    async #onSubmit(event) {
      event.preventDefault();
      if (this.#busy) return;

      const input = this.querySelector('.ff-discount__input');
      const code = (input?.value || '').trim();
      if (!code) {
        input?.focus();
        return;
      }

      const applied = this.#applied;
      if (applied.some((c) => sameCode(c, code))) {
        this.#showMessage('');
        return;
      }

      const strings = this.#strings;
      this.#setBusy(true);
      try {
        const data = await updateDiscounts([...applied, code]);
        const entry = (data.discount_codes || []).find((dc) => sameCode(dc.code, code));
        if (entry && entry.applicable) {
          this.#showMessage('');
          return;
        }
        // Drop the rejected code so checkout does not carry it along.
        await updateDiscounts(applied);
        this.#showMessage(strings.invalid || 'That code cannot be applied to this cart.', { code });
      } catch (err) {
        console.error('[ff-discount]', err);
        this.#showMessage(strings.failed || 'Could not apply the code. Please try again.', { code });
      } finally {
        this.#fresh().#setBusy(false);
      }
    }

    async #onClick(event) {
      const btn = event.target.closest('[data-ff-discount-remove]');
      if (!btn || this.#busy) return;
      event.preventDefault();

      const remove = btn.dataset.ffDiscountRemove;
      const remaining = this.#applied.filter((c) => !sameCode(c, remove));

      this.#setBusy(true);
      try {
        await updateDiscounts(remaining);
      } catch (err) {
        console.error('[ff-discount][remove]', err);
        this.#showMessage(this.#strings.failed || 'Could not remove the code. Please try again.');
      } finally {
        this.#fresh().#setBusy(false);
      }
    }
  }

  window.FFDiscountCode = FFDiscountCode;
  customElements.define('ff-discount-code', FFDiscountCode);
})();
